const TEN = 10;
const SCIENTIFIC_INTETVAL_1 = 0.2;
const SCIENTIFIC_INTETVAL_2 = 0.4;
const SCIENTIFIC_INTETVAL_3 = 0.5;
const SCIENTIFIC_INTETVAL_4 = 0.6;
const SCIENTIFIC_INTETVAL_5 = 0.8;

/**
 * 根据精度，向上取整
 */
export function ceil(value: number, digit?: number) {
  if (value === 0) {
    return 0;
  }
  const TEN = 10;
  const absValue = Math.abs(value);
  let log10 = Math.floor(Math.log10(absValue)) || 0;
  if (typeof digit === 'number' && !Number.isNaN(digit)) {
    log10 = digit;
  }
  const precision = TEN ** log10;
  return Math.ceil(value / precision) * precision;
}

/**
 * 根据精度，向下取整
 */
export function floor(value: number, digit?: number) {
  if (value === 0) {
    return 0;
  }
  const TEN = 10;
  const absValue = Math.abs(value);
  let log10 = Math.floor(Math.log10(absValue)) || 0;
  if (typeof digit === 'number' && !Number.isNaN(digit)) {
    log10 = digit;
  }
  const precision = TEN ** log10;
  return Math.floor(value / precision) * precision;
}

/**
 * Copy from https://github.com/apache/echarts/blob/5.3.3/src/util/number.ts#L412
 */
function quantityExponent(val) {
  if (val === 0) {
    return 0;
  }

  let exp = Math.floor(Math.log(val) / Math.LN10);
  /**
   * exp is expected to be the rounded-down result of the base-10 log of val.
   * But due to the precision loss with Math.log(val), we need to restore it
   * using 10^exp to make sure we can get val back from exp. #11249
   */

  if (val / 10 ** exp >= 10) {
    exp++;
  }

  return exp;
}

/**
 * https://stackoverflow.com/questions/17369098/simplest-way-of-getting-the-number-of-decimals-in-a-number-in-javascript
 */
function countDecimals(num: number) {
  if (Math.floor(num.valueOf()) === num.valueOf()) {
    return 0;
  }
  return num.toString().split('.')[1].length || 0;
}

/** 预设比较美观的区间数值 */
const NICE_INTERVAL_VALUES = [
  1, 1.2, 1.4, 1.5, 1.6, 1.8, 2, 2.2, 2.5, 3, 3.5, 4, 4.5, 5, 6, 7, 8, 9, 10
];

/**
 * 为给定的数字取一个美观的数值
 *
 * - 参考 https://github.com/apache/echarts/blob/5.3.3/src/util/number.ts#L440
 */
export function nice(val: number, round = false) {
  if (val === 0) {
    return 0;
  }

  const exponent = quantityExponent(val);
  const exp10 = 10 ** exponent;
  // 1 <= f < 10
  const f = val / exp10;

  let nf = f;

  if (round) {
    nf = Math.round(f);
  }

  for (let i = 0; i < NICE_INTERVAL_VALUES.length; i += 1) {
    const item = NICE_INTERVAL_VALUES[i];
    if (item >= nf) {
      nf = item;
      break;
    }
  }

  const decimals = countDecimals(nf);
  // Fix 3 * 0.1 === 0.30000000000000004 issue (see IEEE 754).
  val = nf * exp10;

  // 20 is the uppper bound of toFixed.
  // return exponent >= -20 ? +val.toFixed(exponent < 0 ? -exponent : 0) : val;
  return exponent >= -20 ? +val.toFixed(Math.max(0, decimals + -exponent)) : val;
}

export function isInt(num: number): boolean {
  return num % 1 === 0;
}

export function isNumber(num: unknown): boolean {
  return typeof num === 'number' && !Number.isNaN(num);
}

/**
 * 将数值限制在下限值和上限值构成的范围内。
 *
 * @param value - 需要限制的值。
 * @param lower - 下限值。
 * @param upper - 上限值。
 * @returns 限制后得到的数值。
 */
export function clamp(value: number, lower: number, upper: number) {
  const lowerClampedValue = Math.max(value, lower);
  return Math.min(lowerClampedValue, upper);
}
